
import { useRef } from "react";
import { Investor, Contact } from "@/types/investor";
import { useSupabaseSync } from "@/hooks/useSupabaseSync";
import { useDebugHookUtils } from "./useDebugHookUtils";
import { useBatchProcessingState } from "./useBatchProcessingState";
import { useSaveOperations } from "./useSaveOperations";
import { useContextData } from "./useContextData";
import { usePendingSaves } from "./usePendingSaves";

/**
 * Hook for saving investor data to Supabase and localStorage
 */
export const useSaveInvestorData = () => {
  const { syncToSupabase, syncContextToSupabase } = useSupabaseSync();
  
  // Use debug utilities
  useDebugHookUtils('useSaveInvestorData');
  
  // Use batch processing state
  const {
    isBatchProcessing,
    pendingSaves,
    saveTimerRef,
    startBatchProcessing,
    endBatchProcessing,
    isSyncDisabled,
    addToPendingSaves,
    pendingCount
  } = useBatchProcessingState();
  
  // Use save operations
  const {
    isSaving,
    saveDataToSupabaseAndLocal
  } = useSaveOperations();
  
  // Use context data
  const {
    saveContextData
  } = useContextData();
  
  // Use pending saves
  const {
    processPendingSaves,
    flushPendingSaves: flushPendingSavesBase,
    saveInvestorUpdate: saveInvestorUpdateBase
  } = usePendingSaves(pendingSaves, saveDataToSupabaseAndLocal);
  
  // Wrapper for saveInvestorUpdate
  const saveInvestorUpdate = async (
    investor: Investor,
    allInvestors: Investor[],
    contacts: Contact[],
    processedIds: Set<string>
  ): Promise<boolean> => {
    return saveInvestorUpdateBase(
      investor, 
      allInvestors, 
      contacts, 
      processedIds,
      isBatchProcessing,
      saveTimerRef,
      addToPendingSaves
    );
  };
  
  // Wrapper for flushPendingSaves
  const flushPendingSaves = async (
    allInvestors?: Investor[],
    contacts?: Contact[],
    processedIds?: Set<string>
  ): Promise<boolean> => {
    // Clear any pending save timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    
    return flushPendingSavesBase(allInvestors, contacts, processedIds);
  };

  return {
    isSaving,
    saveDataToSupabaseAndLocal,
    saveContextData,
    saveInvestorUpdate,
    startBatchProcessing,
    endBatchProcessing,
    flushPendingSaves,
    pendingCount,
    isSyncDisabled
  };
};
